/* # FaultReportDetail Page
   - Displays the detailed view of a single fault report
   - Enhanced handleDeleteReport with thorough cascaded cleanup of photos, comments, mentions, and notifications
*/
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuArrowLeft, LuMapPin, LuCalendar, LuUser, LuSettings, LuTrash2, LuPencil, LuX, LuSave } from 'react-icons/lu';
import { useAuth } from '../contexts/AuthContext';
import { canModifyFaultReport, isManagementTier } from '../utils/roleCheck';
import { createFaultStatusNotifications } from '../utils/notificationHelpers';
import { toast } from '../hooks/use-toast';
import AttachmentList from '../components/FaultReports/AttachmentList';
import CommentSection from '../components/FaultReports/CommentSection';
import StatusTimeline from '../components/FaultReports/StatusTimeline';
import EditFaultForm from '../components/FaultReports/EditFaultForm';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle } from
'@/components/ui/alert-dialog';

const priorityColors = {
  Low: 'bg-gray-100 text-gray-700',
  Medium: 'bg-blue-100 text-blue-700',
  High: 'bg-orange-100 text-orange-700',
  Urgent: 'bg-red-100 text-red-700'
};

const statusColors = {
  New: 'bg-purple-100 text-purple-700',
  Acknowledged: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  Waiting: 'bg-gray-100 text-gray-700',
  Resolved: 'bg-green-100 text-green-700',
  Closed: 'bg-gray-100 text-gray-500'
};

export default function FaultReportDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fault, setFault] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [building, setBuilding] = useState(null);
  const [reporterName, setReporterName] = useState('Loading...');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (id && user) loadFaultDetail();
  }, [id, user]);

  const loadFaultDetail = async () => {
    try {
      setLoading(true);
      const { data: faultData, error: faultError } = await window.ezsite.apis.tablePage(70649, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: Number(id) }]
      });

      if (faultError) throw faultError;
      const faultReport = faultData?.List?.[0];

      if (!faultReport) {
        toast({ title: t('common.error'), description: t('faultReports.notFound'), variant: 'destructive' });
        navigate('/fault-reports');
        return;
      }

      setFault(faultReport);
      setSelectedStatus(faultReport.status);

      const creatorId = faultReport.created_by || faultReport.reporter_id;
      const userIsMgmt = isManagementTier(user?.role);

      if (!creatorId) {
        setReporterName('System');
      } else if (Number(creatorId) === Number(user.id)) {
        setReporterName('You');
      } else if (userIsMgmt || !faultReport.is_anonymous) {
        try {
          const { data: userData } = await window.ezsite.apis.tablePage(70648, {
            PageNo: 1,
            PageSize: 1,
            Filters: [{ name: 'id', op: 'Equal', value: Number(creatorId) }]
          });
          const foundUser = userData?.List?.[0];
          setReporterName(foundUser?.name || `User #${creatorId}`);
        } catch (e) {
          setReporterName(`User #${creatorId}`);
        }
      } else {
        setReporterName('Anonymous');
      }

      const { data: photosData } = await window.ezsite.apis.tablePage(70650, {
        PageNo: 1,
        PageSize: 50,
        Filters: [{ name: 'fault_report_id', op: 'Equal', value: Number(id) }]
      });
      setAttachments(photosData?.List || []);

      if (faultReport.building_id) {
        const { data: bRes } = await window.ezsite.apis.tablePage(70647, {
          PageNo: 1,
          PageSize: 1,
          Filters: [{ name: 'id', op: 'Equal', value: Number(faultReport.building_id) }]
        });
        setBuilding(bRes?.List?.[0]);
      }
    } catch (error) {
      console.error('Failed to load fault detail:', error);
      toast({ title: t('common.error'), description: t('faultReports.loadDetailsFailed'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!isManagementTier(user?.role)) return;
    if (newStatus === 'In Progress') {
      if (!confirm(t('faultReports.confirmProgress'))) {
        setSelectedStatus(fault.status);
        return;
      }
    }

    const oldStatus = fault.status;
    try {
      const history = JSON.parse(fault.status_history || '[]');
      history.push({
        status: newStatus,
        timestamp: new Date().toISOString(),
        changed_by: user.id
      });

      const { error } = await window.ezsite.apis.tableUpdate(70649, {
        id: Number(fault.id),
        status: newStatus,
        status_history: JSON.stringify(history),
        updated_at: new Date().toISOString()
      });

      if (error) throw error;

      if (oldStatus !== newStatus) {
        await createFaultStatusNotifications(fault, oldStatus, newStatus).catch((e) =>
        console.error('Failed to send status notification:', e)
        );
      }

      toast({ title: t('common.success'), description: t('faultReports.statusUpdated') });
      loadFaultDetail();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({ title: t('common.error'), description: t('faultReports.statusUpdateFailed'), variant: 'destructive' });
    }
  };

  const handleUpdateReport = async (formData, newFiles, deletedIds) => {
    try {
      const now = new Date().toISOString();
      const { error: updateError } = await window.ezsite.apis.tableUpdate(70649, {
        id: Number(fault.id),
        ...formData,
        updated_at: now
      });
      if (updateError) throw updateError;

      if (deletedIds.length > 0) {
        await Promise.all(deletedIds.map((dId) => window.ezsite.apis.tableDelete(70650, { ID: Number(dId) })));
      }

      if (newFiles.length > 0) {
        await Promise.all(newFiles.map((file) => window.ezsite.apis.tableCreate(70650, {
          fault_report_id: Number(fault.id),
          file_url: file.url,
          file_name: file.name,
          uploaded_by: Number(user.id),
          created_at: now
        })));
      }

      toast({ title: t('common.success'), description: t('faultReports.reportUpdated') });
      setIsEditing(false);
      loadFaultDetail();
    } catch (error) {
      console.error('Failed to update report:', error);
      toast({ title: t('common.error'), description: t('faultReports.updateFailed') });
    }
  };

  const handleDeleteReport = async () => {
    // Permission check - only authorized users can delete
    if (!canManage) {
      toast({
        title: t('common.error'),
        description: t('faultReports.noPermission'),
        variant: 'destructive'
      });
      return;
    }

    setShowDeleteDialog(false);
    setDeleting(true);

    try {
      const reportId = Number(fault.id);
      console.log(`[Delete] Starting cascade deletion for Fault Report ID: ${reportId}`);

      // Step 1: Delete fault comment attachments (Table 71362)
      const { data: commentsData } = await window.ezsite.apis.tablePage(70651, {
        PageNo: 1,
        PageSize: 500,
        Filters: [{ name: 'fault_report_id', op: 'Equal', value: reportId }]
      });

      if (commentsData?.List?.length > 0) {
        console.log(`[Delete] Found ${commentsData.List.length} comments to process`);

        for (const comment of commentsData.List) {
          const commentId = Number(comment.id);

          // Delete comment attachments
          const { data: attachmentsData } = await window.ezsite.apis.tablePage(71362, {
            PageNo: 1,
            PageSize: 100,
            Filters: [{ name: 'comment_id', op: 'Equal', value: commentId }]
          });

          if (attachmentsData?.List?.length > 0) {
            console.log(`[Delete] Deleting ${attachmentsData.List.length} attachments for comment ${commentId}`);
            await Promise.all(
              attachmentsData.List.map((att) =>
              window.ezsite.apis.tableDelete(71362, { ID: Number(att.id) })
              )
            );
          }

          // Delete comment mentions (Table 70654)
          const { data: mentionsData } = await window.ezsite.apis.tablePage(70654, {
            PageNo: 1,
            PageSize: 100,
            Filters: [
            { name: 'entity_type', op: 'Equal', value: 'comment' },
            { name: 'entity_id', op: 'Equal', value: commentId, connector: 'And' }]

          });

          if (mentionsData?.List?.length > 0) {
            console.log(`[Delete] Deleting ${mentionsData.List.length} mentions for comment ${commentId}`);
            await Promise.all(
              mentionsData.List.map((mention) =>
              window.ezsite.apis.tableDelete(70654, { ID: Number(mention.id) })
              )
            );
          }
        }

        // Step 2: Delete all fault comments (Table 70651)
        console.log(`[Delete] Deleting ${commentsData.List.length} comments`);
        await Promise.all(
          commentsData.List.map((comment) =>
          window.ezsite.apis.tableDelete(70651, { ID: Number(comment.id) })
          )
        );
      }

      // Step 3: Delete fault photos (Table 70650)
      const { data: photosData } = await window.ezsite.apis.tablePage(70650, {
        PageNo: 1,
        PageSize: 100,
        Filters: [{ name: 'fault_report_id', op: 'Equal', value: reportId }]
      });

      if (photosData?.List?.length > 0) {
        console.log(`[Delete] Deleting ${photosData.List.length} photos`);
        await Promise.all(
          photosData.List.map((photo) =>
          window.ezsite.apis.tableDelete(70650, { ID: Number(photo.id) })
          )
        );
      }

      // Step 4: Delete related notifications (Table 70655)
      const { data: notificationsData } = await window.ezsite.apis.tablePage(70655, {
        PageNo: 1,
        PageSize: 500,
        Filters: [{ name: 'link', op: 'StringContains', value: `/fault-reports/${reportId}` }]
      });

      if (notificationsData?.List?.length > 0) {
        console.log(`[Delete] Deleting ${notificationsData.List.length} notifications`);
        await Promise.all(
          notificationsData.List.map((notif) =>
          window.ezsite.apis.tableDelete(70655, { ID: Number(notif.id) })
          )
        );
      }

      // Step 5: Finally, delete the fault report itself (Table 70649)
      console.log(`[Delete] Deleting fault report ${reportId}`);
      const { error: deleteError } = await window.ezsite.apis.tableDelete(70649, { ID: reportId });

      if (deleteError) {
        throw new Error(deleteError);
      }

      console.log(`[Delete] Successfully deleted fault report ${reportId} and all related data`);

      toast({
        title: t('common.success'),
        description: t('faultReports.reportDeleted')
      });

      // Redirect to fault reports list
      navigate('/fault-reports');
    } catch (error) {
      console.error('[Delete] Cascade deletion failed:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('faultReports.deleteFailed'),
        variant: 'destructive'
      });
      setDeleting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
    </div>);

  if (!fault) return null;

  const canManage = canModifyFaultReport(user, fault);
  const userIsMgmt = isManagementTier(user?.role);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate('/fault-reports')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 font-semibold transition-colors group">

          <LuArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          {t('faultReports.backToList')}
        </button>

        {canManage && !isEditing &&
        <button
          onClick={() => setIsEditing(true)}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all font-bold text-sm shadow-sm active:scale-95">

          <LuPencil className="w-4 h-4" />
          {t('faultReports.editReport')}
        </button>
        }
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {isEditing ?
          <div className="bg-white rounded-[2.5rem] border border-blue-100 p-10 shadow-xl shadow-blue-50/50 animate-in zoom-in duration-300">
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">{t('faultReports.editReport')}</h2>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">{t('faultReports.modifyDetails')}</p>
                </div>
                <button onClick={() => setIsEditing(false)} className="p-3 hover:bg-gray-100 rounded-full transition-colors">
                  <LuX className="w-6 h-6 text-gray-400" />
                </button>
              </div>
              <EditFaultForm fault={fault} existingAttachments={attachments} onSubmit={handleUpdateReport} onCancel={() => setIsEditing(false)} />
            </div> :

          <div className="bg-white rounded-[2.5rem] border border-gray-100 p-10 shadow-sm space-y-8">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${priorityColors[fault.priority]}`}>{t(`faultReports.priority${fault.priority}`)}</span>
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusColors[fault.status]}`}>{t(`faultReports.status${fault.status.replace(' ', '')}`)}</span>
              </div>
              <h1 className="text-3xl font-extrabold text-gray-900 leading-tight">{fault.title}</h1>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-8 border-y border-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-xl"><LuCalendar className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t('faultReports.reported')}</p>
                    <p className="text-sm font-bold text-gray-700">{new Date(fault.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 rounded-xl"><LuMapPin className="w-5 h-5 text-purple-600" /></div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t('faultReports.location')}</p>
                    <p className="text-sm font-bold text-gray-700 truncate">{building?.name || t('faultReports.propertyWide')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded-xl"><LuUser className="w-5 h-5 text-indigo-600" /></div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t('faultReports.reporter')}</p>
                    <p className="text-sm font-bold text-gray-700">{reporterName}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">{t('faultReports.issueDescription')}</h3>
                <p className="text-gray-600 leading-relaxed bg-gray-50/50 p-8 rounded-3xl border border-gray-100 whitespace-pre-wrap">{fault.description}</p>
              </div>
              {attachments.length > 0 &&
            <div className="space-y-4 pt-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">{t('faultReports.attachments')} ({attachments.length})</h3>
                  <AttachmentList attachments={attachments} />
                </div>
            }
            </div>
          }
        </div>

        <div className="space-y-6">
          {canManage &&
          <div className="bg-white rounded-[2.5rem] border border-blue-100 p-8 space-y-6 shadow-sm shadow-blue-50 ring-1 ring-blue-50/50">
              <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                <div className="p-2 bg-blue-600 rounded-xl shadow-lg shadow-blue-200">
                  <LuSettings className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-extrabold text-gray-900">{t('faultReports.administrativeActions')}</h3>
              </div>
              {userIsMgmt &&
            <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{t('faultReports.updateProgress')}</label>
                  <div className="flex flex-col gap-3">
                    <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-700 focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all cursor-pointer">

                      <option value="New">{t('faultReports.statusNew')}</option>
                      <option value="Acknowledged">{t('faultReports.statusAcknowledged')}</option>
                      <option value="In Progress">{t('faultReports.statusInProgress')}</option>
                      <option value="Waiting">{t('faultReports.statusWaiting')}</option>
                      <option value="Resolved">{t('faultReports.statusResolved')}</option>
                      <option value="Closed">{t('faultReports.statusClosed')}</option>
                    </select>
                    
                    {selectedStatus !== fault.status &&
                <button
                  onClick={() => handleStatusUpdate(selectedStatus)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all active:scale-95 animate-in slide-in-from-top-2 duration-300">

                        <LuSave className="w-4 h-4" />
                        {t('faultReports.updateStatus')}
                      </button>
                }
                  </div>
                </div>
            }
              <div className="pt-4">
                <button
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleting}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-2xl transition-all font-bold text-sm shadow-sm active:scale-95 disabled:opacity-50">

                  <LuTrash2 className="w-4 h-4" />
                  {deleting ? t('faultReports.deleting') : t('faultReports.deleteReport')}
                </button>
              </div>
            </div>
          }
          <StatusTimeline history={fault.status_history} />
        </div>
      </div>

      <div className="mt-8">
        <CommentSection faultId={Number(fault.id)} isManager={userIsMgmt} />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-red-100 rounded-full">
                <LuTrash2 className="w-6 h-6 text-red-600" />
              </div>
              <AlertDialogTitle className="text-xl font-bold">
                {t('faultReports.deleteConfirmTitle') || 'Delete Fault Report?'}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base leading-relaxed pt-2">
              {t('faultReports.deleteConfirmMessage') ||
              'This action cannot be undone. This will permanently delete the fault report and all related data including comments, photos, and attachments.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel disabled={deleting} className="font-bold">
              {t('common.cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReport}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 font-bold">

              {deleting ?
              <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {t('faultReports.deleting') || 'Deleting...'}
                </div> :

              <div className="flex items-center gap-2">
                  <LuTrash2 className="w-4 h-4" />
                  {t('faultReports.confirmDelete') || 'Delete'}
                </div>
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>);
}